import * as React from "react";
import { useSelector } from "react-redux";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import Chart from "chart.js/auto";
import { Box, Grow, Paper } from "@mui/material";
import "./style.css";
import Dialog from "@mui/material/Dialog";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import Slide from "@mui/material/Slide";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import moment from "moment";
import { getOutstandingItems } from "../Api/index";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction='up' ref={ref} {...props} />;
});
Chart.register(ChartDataLabels);
Chart.defaults.set("plugins.datalabels", {
  color: "#000",
  backgroundColor: "#ccc",
});

const columns = [
  {
    field: "date",
    headerName: "Value Date",
    width: 130,
  },
  {
    field: "description",
    headerName: "Description",
    flex: 1,
    minWidth: 260,
  },
  {
    field: "amount",
    headerName: "Amount (GHS)",
    type: "number",
    width: 160,
    valueFormatter: (params) =>
      Number(params.value || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
  },
  {
    field: "aging",
    headerName: "Age (days)",
    type: "number",
    width: 110,
  },
];
function createData(id, date, description, amount, aging) {
  return { id, date, description, amount, aging };
}
export default function Barchart({ branches = [] }) {
  const [open, setOpen] = React.useState(false);
  const [branch, setBranch] = React.useState("");
  const [limit, setLimit] = React.useState(null);
  // The clicked branch's items, fetched only when a bar is clicked
  const [items, setItems] = React.useState([]);
  const [loadingItems, setLoadingItems] = React.useState(false);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const locations = useSelector((state) => state.location);

  const locationNames = locations.map((loc) => loc.name.toLowerCase());

  // A report saves its items under "<branch> head office" and
  // "head office <branch>"; both count towards the branch's bar
  const statementNames = (loc) => [`${loc} head office`, `head office ${loc}`];
  const totalFor = (loc, field) =>
    branches
      .filter((row) => statementNames(loc).includes(row.branch))
      .reduce((sum, row) => sum + row[field], 0);

  const totalOutstandings = locationNames.map((loc) => totalFor(loc, "count"));
  const agedOutstandings = locationNames.map((loc) => totalFor(loc, "aged"));

  const labels = locationNames.map(
    (loc) => loc.charAt(0).toUpperCase() + loc.slice(1)
  );

  const outstandings = {
    labels,
    datasets: [
      {
        label: "outstandings",
        data: totalOutstandings,
        borderColor: "#000000",
        backgroundColor: "#365062",
      },
    ],
  };

  const aged = {
    labels,
    datasets: [
      {
        label: "overdue outstanding",
        data: agedOutstandings,
        borderColor: "#46607b",
        backgroundColor: "#ffbc4a",
      },
    ],
  };

  const chartClickHandler = (label, agingLimit = null) => {
    const loc = label.toLowerCase();
    setBranch(loc);
    setLimit(agingLimit);
    setItems([]);
    setLoadingItems(true);
    handleClickOpen();
    getOutstandingItems(statementNames(loc), agingLimit)
      .then(({ data }) => setItems(data.results || []))
      .catch(() => setItems([]))
      .finally(() => setLoadingItems(false));
  };

  const commonOptions = (titleText, agingLimit) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        display: true,
        anchor: "end",
        align: "top",
        color: "#000",
        font: { weight: "bold", size: 14 },
      },
      legend: { display: false },
      title: { display: true, text: titleText, color: "#21313C" },
    },
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const clickedIndex = elements[0].index;
        const label = labels[clickedIndex];
        chartClickHandler(label, agingLimit);
      }
    },
  });

  const outstandingsOptions = commonOptions(
    "Current Total Outstandings Per Branch",
    null
  );
  const agedOption = commonOptions(
    "Number of Overdue Outstandings Per Branch",
    30
  );

  const rows = items.map((data) =>
    createData(
      data._id,
      moment(data.value_date).format("DD-MM-YYYY"),
      data.description,
      data.amount,
      data.aging
    )
  );

  const drillTotal = items.reduce(
    (total, data) => total + (Number(data.amount) || 0),
    0
  );

  const chartCard = {
    p: 1.5,
    borderRadius: "14px",
    border: "1px solid #e4e9ee",
    height: { xs: 320, md: "46vh" },
    minHeight: 300,
    minWidth: 0,
  };

  return (
    <div>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
          gap: 2,
          mb: 1,
        }}>
        <Grow in timeout={600}>
          <Paper elevation={0} sx={chartCard}>
            <Bar
              data={outstandings}
              options={outstandingsOptions}
              style={{ cursor: "pointer" }}
            />
          </Paper>
        </Grow>
        <Grow in timeout={800}>
          <Paper elevation={0} sx={chartCard}>
            <Bar
              data={aged}
              options={agedOption}
              style={{ cursor: "pointer" }}
            />
          </Paper>
        </Grow>
      </Box>
      <Typography sx={{ fontSize: 12, color: "#667085" }}>
        Click a bar to see that branch&apos;s items. Age is counted from the
        value date to the reconciliation date.
      </Typography>
      <Dialog
        fullScreen
        style={{ zIndex: 3000 }}
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}>
        <AppBar
          elevation={0}
          sx={{ position: "relative", backgroundColor: "#21313c" }}>
          <Toolbar sx={{ gap: 1.5 }}>
            <IconButton
              edge='start'
              color='inherit'
              onClick={handleClose}
              aria-label='close'>
              <CloseIcon />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                noWrap
                sx={{ fontWeight: 800, textTransform: "capitalize" }}>
                {branch}
                {limit ? ` · older than ${limit} days` : ""}
              </Typography>
              <Typography sx={{ fontSize: 13, opacity: 0.8 }}>
                {rows.length} item{rows.length === 1 ? "" : "s"} · GHS{" "}
                {drillTotal.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
            </Box>
          </Toolbar>
        </AppBar>
        <DataGrid
          columns={columns}
          rows={rows}
          loading={loadingItems}
          slots={{ toolbar: GridToolbar }}
          rowSelection={false}
        />
      </Dialog>
    </div>
  );
}
